import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { type JSX, ReactNode, useState } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_DELETE_REMOVE } from 'Config/Icons';

import styles from './ConfirmationModal.module.css';
import { Modal } from '../Modal';

const TEXT_TO_TYPE_TO_CONFIRM_DELETE = 'DELETE';

export enum ConfirmationModalOperationType {
    /** The operation is not destructive, but still has major consequences. For example: toggling controls. */
    NON_DESTRUCTIVE,
    /** The operation is destructive, but it is not a deletion (from the user's perspective). For example: retiring a policy. Usage of this type should be rare. */
    DESTRUCTIVE,
    /** The operation is a destructive action that specifically deletes one or more things. The user is required to type "delete" before the Delete button is enabled, as the operation could have major consequences. For example: deleting a custom control. */
    DELETE_AFTER_TYPING_TO_CONFIRM,
    /** The operation is a destructive action that specifically deletes one or more things. The user is NOT required to type "delete" before the Delete button is enabled, as the operation won't have major consequences. For example: deleting information on a page before the page is submitted. */
    DELETE_WITHOUT_TYPING_TO_CONFIRM,
}

interface ConfirmationModalBaseProps {
    isLoading?: false;
    /** The nature of the operation that will be performed if the user clicks the confirmation button. */
    operationType: ConfirmationModalOperationType;
    /**
     * The text displayed in the modal header. Will be rendered within `<Text variant="Header2">`.
     *
     * IMPORTANT: for consistency, this should be of the form: "<operation> <entity/entities>". For example: "Delete Risk", Retire Policy", or "Toggle Controls".
     */
    headerText: string;
    /**
     * The text displayed at the top of the modal body. Will be rendered as bold text within `<Text variant="Text2">`.
     * Reiterates the operation the user is about to take.
     *
     * IMPORTANT: for consistency:
     * * This should be of the form: "Are you sure you want to...?".
     * * Entity names should be in quotes. For example: `Are you sure you want to delete "Custom Data Backup Control"?`.
     */
    areYouSureText: string;
    /**
     * The text displayed within the modal body (directly below the `areYouSureText`).
     * Details the consequences of the operation the user is about to take.
     *
     * IMPORTANT:
     * * For consistent structure:
     * * * This should contain an unordered list if and only if there are several consequences to explain. (For example, deleting a control will also remove any mappings of the control to several other types of entities.)
     * * * Otherwise, it should be a `Text` (using the default `variant`).
     * * For consistent styling:
     * * * `Text` (using the default `variant`) should be used for all text.
     */
    children: ReactNode;
    /**
     * The operation to perform when the user clicks the confirmation button.
     * IMPORTANT: The success message should reflect the header text. For example: If the header text is "Delete Risk", the success message should be "Risk deleted.".
     *
     * @returns A message that will be displayed in a "success" banner after the operation is performed successfully. Note that if this function throws an error, the error message will be displayed in a "failure" banner.
     */
    performOperation: () => Promise<string | JSX.Element>;
    /**
     * Called when the user clicks the "Close" button or clicks outside of the modal.
     */
    hideModal: () => void;
    /**
     * If defined, this function is called if the `performOperation` Promise is rejected (i.e., if the network request fails).
     */
    onOperationFailed?: () => void;
}

interface ConfirmationModalGenericConfirmationProps extends ConfirmationModalBaseProps {
    /**
     * Used when the user needs to confirm an operation that does not delete/destroy, but still has major consequences. For example: toggling controls.
     */
    operationType: ConfirmationModalOperationType.NON_DESTRUCTIVE;
    /**
     * The text for the button that confirms the operation the user is about to take.
     * For consistency, this text should be Capitalized. For example: "Toggle".
     */
    buttonText: string;
    /**
     * The text for the button that displays while the operation is being performed.
     * For consistency, this text should be Capitalized. For example: "Toggling...".
     */
    buttonLoadingText: string;
    /**
     * The icon for the button that confirms the operation the user is about to take.
     * `ICON_POSITIVE` is a good generic option if there is not a better alternative.
     */
    buttonIcon: IconDefinition;
}

interface ConfirmationModalDestructiveOperationProps extends ConfirmationModalBaseProps {
    /**
     * Used when the user needs to confirm a destructive operation that is _not_ a deletion. For example: retiring a policy.
     * If you find yourself including "delete" in the header text and/or button text, you should probably use the 'delete' `operationType` instead.
     */
    operationType: ConfirmationModalOperationType.DESTRUCTIVE;
    /**
     * The text for the button that confirms the operation the user is about to take.
     * For consistency, this text should be Capitalized. For example: "Retire".
     */
    buttonText: string;
    /**
     * The text for the button that displays while the operation is being performed.
     * For consistency, this text should be Capitalized. For example: "Retiring...".
     */
    buttonLoadingText: string;
    /**
     * The icon for the button that confirms the operation the user is about to take.
     * `ICON_DELETE_REMOVE` is a good generic option if there is not a better alternative. But keep in mind that `ICON_DELETE_REMOVE` is always used for the 'delete' `operationType`.
     */
    buttonIcon: IconDefinition;
}

interface ConfirmationModalDeleteAfterTypingToConfirmOperationProps extends ConfirmationModalBaseProps {
    /** The operation is a destructive action that specifically deletes one or more things. The user is required to type "delete" before the Delete button is enabled, as the operation could have major consequences. For example: deleting a custom control. */
    operationType: ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM;
}

interface ConfirmationModalDeleteWithoutTypingToConfirmOperationProps extends ConfirmationModalBaseProps {
    /** The operation is a destructive action that specifically deletes one or more things. The user is NOT required to type "delete" before the Delete button is enabled, as the operation won't have major consequences. For example: deleting information on a page before the page is submitted. */
    operationType: ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM;
}

interface ConfirmationModalLoadingProps {
    isLoading: true;
    /** The text displayed in the modal header. Ensure this matches the `headerText` that is passed in once loading finishes. */
    headerText: string;
    /** Used as the onClick of a "Close" button rendered alongside the loading animation, so the user doesn't get stuck if loading never finishes. */
    hideModal: () => void;

    children?: never;
    /** If defined, this is displayed in place of the loading animation. Pass in a value if loading data for the content of the modal fails. */
    failureMessage?: string;
}

export type ConfirmationModalProps = ConfirmationModalLoadingProps | ConfirmationModalGenericConfirmationProps | ConfirmationModalDeleteAfterTypingToConfirmOperationProps | ConfirmationModalDeleteWithoutTypingToConfirmOperationProps | ConfirmationModalDestructiveOperationProps;

type OperationState =
    | {
          type: 'notPerformed';
      }
    | {
          type: 'inProgress';
      }
    | {
          type: 'performed';
          wasSuccessful: boolean;
          message: string | JSX.Element;
      };

/**
 * Used when the user is attempting to perform an operation that should be confirmed because the operation has major consequences.
 * Displays:
 * * A header.
 * * "Are you sure...?" text.
 * * An explanation of the operation's consequences.
 * * A "CANCEL"/"Close" button.
 * * A confirmation button.
 * * A success or failure banner, once the operation has been performed.
 *
 * If data needs to be loaded before the content of the modal can be displayed, render this same component with `isLoading`, `headerText`, and `hideModal` (plus `failureMessage`, if there was a failure loading data). `Modal` should not be used directly for the loading state, as switching between two different components with the darkened background causes an unpleasant flicker on the screen.
 */
export const ConfirmationModal = (props: ConfirmationModalProps): JSX.Element => {
    const [operationState, setOperationState] = useState<OperationState>({ type: 'notPerformed' });
    const [confirmActionText, setConfirmActionText] = useState('');

    // This must come before anything that reads `props.operationType` (which is not provided while loading).
    if (props.isLoading) {
        return <Modal open isLoading closeModal={props.hideModal} header={props.headerText} failureMessage={props.failureMessage} />;
    }

    const [buttonText, buttonLoadingText, buttonIcon] = (() => {
        switch (props.operationType) {
            case ConfirmationModalOperationType.NON_DESTRUCTIVE:
            case ConfirmationModalOperationType.DESTRUCTIVE:
                return [props.buttonText, props.buttonLoadingText, props.buttonIcon];
            case ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM:
            case ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM:
                return ['Delete', 'Deleting...', ICON_DELETE_REMOVE];
        }
    })();

    const onConfirmButtonClicked = async () => {
        try {
            setOperationState({ type: 'inProgress' });
            const successMessage = await props.performOperation();
            setOperationState({ type: 'performed', wasSuccessful: true, message: successMessage });
        } catch (error) {
            setOperationState({ type: 'performed', wasSuccessful: false, message: error.message });
        }
    };

    const confirmActionTextInsufficient = props.operationType === ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM ? confirmActionText !== TEXT_TO_TYPE_TO_CONFIRM_DELETE : false;

    return (
        <Modal open header={props.headerText}>
            {operationState.type === 'performed' && <Alert type={operationState.wasSuccessful ? 'success' : 'error'}>{operationState.message}</Alert>}

            <Text>
                <b>{props.areYouSureText}</b>
            </Text>

            {/* `fieldset` is used because in rare cases, the `children` supplied include additional form elements that the user should only be able to edit before a successful network request. */}
            <fieldset disabled={operationState.type === 'inProgress' || (operationState.type === 'performed' && operationState.wasSuccessful)}>{props.children}</fieldset>

            {props.operationType === ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM && (
                <div className={styles.confirmationTextTypingContainer}>
                    {/* Although this field is technically required, it looks quite strange being the only form field in the modal (usually) and having the "required" asterisk. And it isn't contained within a Form anyway. So, `required` is not used. */}
                    {/* Currently, all form field labels are in all caps, which makes it very unclear what exactly the user is supposed to type. So, `Text` is used instead of `formFieldLabel`. */}
                    <Text noStyles variant="Text3">{`Type "${TEXT_TO_TYPE_TO_CONFIRM_DELETE}" to confirm.`}</Text>
                    <FormFieldText formFieldId="confirmAction" ariaLabel="Confirm delete" value={confirmActionText} handleChange={(event: React.FormEvent<HTMLInputElement>) => setConfirmActionText(event.currentTarget.value)} />
                </div>
            )}

            <div className={'modalFormButtonContainer'}>
                <Button disabled={operationState.type === 'inProgress'} variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    {operationState.type === 'performed' && operationState.wasSuccessful ? 'Close' : 'CANCEL'}
                </Button>
                <Button variant={props.operationType === ConfirmationModalOperationType.NON_DESTRUCTIVE ? 'primary' : 'danger'} onClick={onConfirmButtonClicked} fontAwesomeImage={buttonIcon} disabled={operationState.type === 'inProgress' || (operationState.type === 'performed' && operationState.wasSuccessful) || confirmActionTextInsufficient} isLoading={operationState.type === 'inProgress'} loadingText={buttonLoadingText}>
                    {buttonText}
                </Button>
            </div>
        </Modal>
    );
};
